import type { ProposalData } from '../types/portal';
import type { WizardStep } from '../data/wizardSteps';
import type { DocumentSlotRow } from '../api/types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type StepErrors = Record<string, string>;

export interface ValidationContext {
  documentSlots: DocumentSlotRow[];
  maxDurationMonths: number;
}

const DEFAULT_CONTEXT: ValidationContext = {
  documentSlots: [],
  maxDurationMonths: 24
};

const isNumeric = (value: string) => value.trim() !== '' && !Number.isNaN(Number(value.trim()));

/**
 * Client-side validation for one step. It mirrors the checks ResProposal.aspx,
 * ResSubForm.aspx and ResConsentForm.aspx made before moving on, which
 * Research.fn_ReadinessIssues enforces server-side — this copy exists to give
 * immediate feedback, not to be the authority.
 */
export function validateStep(
  step: WizardStep,
  data: ProposalData,
  context: ValidationContext = DEFAULT_CONTEXT
): StepErrors {
  const errors: StepErrors = {};
  const need = (key: string, value: string, message: string) => {
    if (!value.trim()) errors[key] = message;
  };

  switch (step.kind) {
    case 'identification':
      need('title', data.title, 'Please enter title of research.');
      if (!errors.title && isNumeric(data.title))
        errors.title = 'Please enter valid title of research.';
      break;

    case 'studyPeriod': {
      need('startDate', data.startDate, 'Please enter proposed starting date.');
      const max = context.maxDurationMonths || 24;
      const months = Number(data.durationMonths);
      if (!data.durationMonths.trim())
        errors.durationMonths = 'Please enter duration of Research Project in number of months.';
      else if (Number.isNaN(months) || !Number.isInteger(months) || months < 1)
        errors.durationMonths = 'Please enter valid duration of Research Project in number of months.';
      else if (months > max)
        errors.durationMonths = `Duration of Research Project cannot be more than ${max} months.`;
      break;
    }

    case 'text':
      (step.textareas ?? []).forEach((area) => {
        const value =
          area.source === 'narrative'
            ? (data.narratives[area.key] ?? '')
            : ((data as unknown as Record<string, string>)[area.key] ?? '');
        if (!area.optional) need(area.key, value, `Please enter ${area.label}.`);
        if (
          area.key === 'references' &&
          value.split('\n').filter((line) => line.trim()).length > 10
        ) {
          errors.references = 'Not more than 10 references are allowed.';
        }
      });
      break;

    case 'grant':
      if (!data.grantRequired)
        errors.grantRequired = 'Indicate whether a grant is required from SMDC.';
      if (data.grantRequired === 'yes') {
        const amount = Number(data.grantAmount);
        if (!data.grantAmount.trim() || Number.isNaN(amount) || amount <= 0)
          errors.grantAmount = 'Please enter grant amount.';
      }
      break;

    case 'principalInvestigator':
      need('piName', data.piName, 'Please enter name of investigator.');
      need('piTitle', data.piTitle, 'Please enter Title/Position of investigator.');
      need('piInstitution', data.piInstitution, 'Please enter Institution.');
      need('piContact', data.piContact, 'Please enter Investigator Contact no.');
      if (!errors.piContact && !isNumeric(data.piContact))
        errors.piContact = 'Please enter valid principal investigator Contact no.';
      need('piEmail', data.piEmail, 'Please enter Investigator Email.');
      if (!errors.piEmail && !EMAIL_RE.test(data.piEmail.trim()))
        errors.piEmail = 'Please enter a valid Email address.';
      break;

    case 'documents': {
      const missing = context.documentSlots.filter(
        (slot) => slot.IsRequired && !data.documents[slot.DocCode]
      );
      if (missing.length > 0)
        errors.documents = `Please upload the file for ${missing.map((slot) => slot.DocName).join(', ')}.`;
      if (data.coverLetter.required && !data.coverLetter.uploaded)
        errors.coverLetter = 'Please upload the Cover Letter.';
      break;
    }

    case 'declaration':
      if (!data.agreed) errors.agreed = 'Please agree to Declaration.';
      break;

    default:
      break;
  }

  return errors;
}
